import type { MembershipsCreatorApi } from '@fourthwall/services/dist/MembershipsCreatorApiClient'
import type { MembershipsCreatorV2ApiClient } from '@fourthwall/services/dist/MembershipsCreatorV2ApiClient'
import type { useFormik } from '@fourthwall/utils/dist/formik'
import { fixRounding } from '@fourthwall/utils/dist/number'

import type { EnabledCurrencies, PriceRates } from '../../../../types'
import { getExchangeRate } from '../../utils'
import { FormFields, type FormValues } from './types'

export const fillCurrencyInputsWithFetchedPriceRates = (
  form: ReturnType<typeof useFormik<FormValues>>,
  priceRates: PriceRates,
  currencies?: (keyof typeof priceRates)[],
) => {
  const keys = currencies || (Object.keys(priceRates) as (keyof typeof priceRates)[])

  keys.forEach((key) => {
    const exchangeRate = getExchangeRate(key.toUpperCase() as EnabledCurrencies[number], priceRates)

    return form.setFieldValue(`${[FormFields.currencies]}.${key}`, exchangeRate.toString())
  })
}

const getImageResourcePayload = (
  removeImage: boolean,
  imageResourceId?: number,
): Parameters<typeof MembershipsCreatorV2ApiClient.updateTier>['1']['imageResource'] => {
  if (imageResourceId) return { type: 'New', id: imageResourceId }

  if (removeImage) return { type: 'Remove' }

  return { type: 'Keep' }
}

export const getFreeTierPayload = (
  formValues: FormValues,
  imageResourceId?: number,
): MembershipsCreatorApi.UpdateFreeTier.RequestBody => {
  const { title, description, removeImage } = formValues

  return {
    name: title,
    description,
    imageResource: getImageResourcePayload(removeImage, imageResourceId),
  }
}

export const getCommonTierPayload = (
  formValues: FormValues,
): Omit<Parameters<typeof MembershipsCreatorV2ApiClient.updateTier>['1'], 'imageResource'> => {
  const {
    title,
    description,
    price,
    discount,
    discountType,
    monthDiscount,
    yearDiscount,
    timeLimit,
    startDate,
    endDate,
    convertInternationalPricing,
    currencies,
    askForShippingAddress,
  } = formValues

  const commonPayload = {
    name: title,
    description,
    pricesAutomaticallyConverted: convertInternationalPricing,
    freeTrial: discount ? discountType === 'trial' : false,
    monthlyAmounts: {
      usd: fixRounding(parseFloat(price) * 100, 2),
      cad: fixRounding(parseFloat(currencies.cad) * 100, 2),
      eur: fixRounding(parseFloat(currencies.eur) * 100, 2),
      gbp: fixRounding(parseFloat(currencies.gbp) * 100, 2),
      aud: fixRounding(parseFloat(currencies.aud) * 100, 2),
      nzd: fixRounding(parseFloat(currencies.nzd) * 100, 2),
      sek: fixRounding(parseFloat(currencies.sek) * 100, 2),
      nok: fixRounding(parseFloat(currencies.nok) * 100, 2),
      dkk: fixRounding(parseFloat(currencies.dkk) * 100, 2),
      pln: fixRounding(parseFloat(currencies.pln) * 100, 2),
      jpy: parseInt(currencies.jpy),
      myr: fixRounding(parseFloat(currencies.myr) * 100, 2),
      sgd: fixRounding(parseFloat(currencies.sgd) * 100, 2),
      mxn: fixRounding(parseFloat(currencies.mxn) * 100, 2),
    },
    askForShippingAddress,
  }

  const getTimeLimitPayload = () => {
    if (timeLimit && startDate && endDate) {
      return {
        timeLimited: {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        },
      }
    }

    return {
      timeLimited: undefined,
    }
  }

  const getDiscountPayload = () => {
    if (discount && monthDiscount && discountType === 'oneTime') {
      return {
        discount: {
          percentOff: {
            monthly: parseInt(monthDiscount),
            annual: yearDiscount ? parseInt(yearDiscount) : undefined,
          },
          ...getTimeLimitPayload(),
        },
      }
    }

    return {
      discount: undefined,
    }
  }

  return {
    ...commonPayload,
    ...getDiscountPayload(),
  }
}

export const getCreateTierPayload = (
  formValues: FormValues,
  imageResourceId?: number,
): Parameters<typeof MembershipsCreatorV2ApiClient.createTier>['0'] => {
  const commonPayload = getCommonTierPayload(formValues)

  return {
    ...commonPayload,
    imageResourceId,
  }
}

export const getUpdateTierPayload = (
  formValues: FormValues,
  imageResourceId?: number,
): Parameters<typeof MembershipsCreatorV2ApiClient.updateTier>['1'] => {
  const { removeImage } = formValues

  const commonPayload = getCommonTierPayload(formValues)

  return {
    ...commonPayload,
    imageResource: getImageResourcePayload(removeImage, imageResourceId),
  }
}
